/**
 * Locale config — the pure, pipe-free core of the localization system.
 *
 * IMPORTANT: this module must stay free of Vue / pipe-store imports. It only
 * uses `Intl` (available in Node and the browser), so it is SSR-safe and can be
 * imported by:
 *   - render components (via `useLocale` → built pipes + editor preview),
 *   - the Pipehouse admin locale page (live "virtual locale" preview),
 *   - Node during the Nuxt static build.
 *
 * Formatting is config-driven (NOT `Intl`-driven) so the symbol, separators,
 * spacing and unit names can all be overridden per (language, geography).
 * Defaults are derived from `Intl` via `formatToParts`, then a stored override
 * is merged on top.
 *
 * Currency note: the runtime always consumes a config resolved to ONE currency
 * (`currencySymbol` / `currencyCode` are the resolved per-currency override).
 * The stored override carries a sparse `currencyOverrides` map keyed by currency
 * code; callers flatten it to the active currency BEFORE merging — see the
 * `resolveLocaleConfig` helper in `@/lib/resolveLocaleConfig` (FE) and the
 * `LocalesService.resolveLocaleConfig` (backend). This module only ever sees the
 * already-resolved scalar shape.
 */

export interface NumberFormatConfig {
  decimalSeparator: string
  thousandsSeparator: string
}

export interface CurrencyFormatRules {
  decimalSeparator: string
  thousandsSeparator: string
  decimals: number
  /** Whether a (non-breaking) space sits between the symbol/code and the number. */
  spaceBetween: boolean
  /** Where the symbol (short) / code (long) sits relative to the number. */
  symbolPosition: 'before' | 'after'
}

export interface UnitNames {
  short: string
  long: string
}

export type MeasurementSystem = 'metric' | 'imperial'

export interface LocaleConfig {
  number: NumberFormatConfig
  /** Locale-wide rules for the symbol form. */
  currencyShort: CurrencyFormatRules
  /** Locale-wide rules for the code form. */
  currencyLong: CurrencyFormatRules
  /** Resolved per-currency override of the short symbol (e.g. `$`, `US$`). */
  currencySymbol?: string
  /** Resolved per-currency override of the long code (e.g. `USD`). */
  currencyCode?: string
  weight: { kilogram: UnitNames; pound: UnitNames }
  height: { centimeter: UnitNames; foot: UnitNames; inch: UnitNames }
  measurementSystem: MeasurementSystem
}

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

/**
 * The shape stored in the DB / edited in the admin form. Every field is
 * optional; `currencySymbol`/`currencyCode` are NOT stored (they are resolved
 * from `currencyOverrides` at flatten time).
 */
export type StoredLocaleOverride = DeepPartial<
  Omit<LocaleConfig, 'currencySymbol' | 'currencyCode'>
> & {
  currencyOverrides?: Record<string, { symbol?: string; code?: string }>
}

/** A deep-partial of the runtime config — already resolved to one currency. */
export type ResolvedLocaleOverride = DeepPartial<LocaleConfig>

// Imperial measurement-system regions (US customary). Everything else metric.
const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM'])

// Whitespace incl. the regular non-breaking space (U+00A0) and the narrow
// no-break space (U+202F) that ICU uses around currency symbols.
const WS_RE = /[\s  ]/

const IMPERIAL_FALLBACK_SHORT: Record<string, string> = {
  kilogram: 'kg',
  pound: 'lb',
  centimeter: 'cm',
  foot: 'ft',
  inch: 'in',
}

/**
 * Build an `Intl` locale tag from a (language, geography) pair.
 *
 * Prefers `${lang}-${GEO}` (e.g. `en-US`). Accepts it only when ICU recognises
 * the region — i.e. the resolved locale keeps the region subtag. Otherwise
 * falls back to `${lang}-${LANG}` (e.g. `en-EN`), preserving the historical
 * behaviour for worldwide / unknown geographies (`ww`, `xx`, …).
 *
 * Verified: `en`+`us`→`en-US`, `en`+`ww`→`en-EN`, `es`+`us`→`es-US`.
 */
/** `tag` when the engine accepts it as a locale, else the bare `en` root. */
function safeLocaleTag(tag: string): string {
  try {
    new Intl.NumberFormat(tag)
    return tag
  } catch {
    return 'en'
  }
}

const RESOLVE_CACHE = new Map<string, string>()

export function resolveLocale(language?: string | null, geography?: string | null): string {
  const lang = (language || 'en').toLowerCase()
  const geo = (geography || '').toUpperCase()
  const cacheKey = `${lang}|${geo}`
  const cached = RESOLVE_CACHE.get(cacheKey)
  if (cached) return cached

  let result: string | undefined
  if (geo.length === 2) {
    const candidate = `${lang}-${geo}`
    try {
      const resolved = new Intl.NumberFormat(candidate).resolvedOptions().locale
      // ICU drops a region it doesn't recognise (e.g. `WW`), so the resolved
      // locale's own region subtag only equals `geo` when the region is real.
      if (new Intl.Locale(resolved).region === geo) result = candidate
    } catch {
      /* malformed tag → fall back */
    }
  }
  // The `${lang}-${LANG}` fallback is historical (`en-EN` for `ww`/`xx`
  // geographies). Validate it too: a malformed language code would otherwise
  // throw out of every `Intl` call below and take the whole page down.
  result ??= safeLocaleTag(`${lang}-${lang.toUpperCase()}`)
  RESOLVE_CACHE.set(cacheKey, result)
  return result
}

/**
 * `new Intl.NumberFormat(...)` with a graceful-degradation chain: each candidate
 * option set is tried in order and the first one the engine accepts wins.
 *
 * Newer `Intl` options THROW on older engines rather than being ignored —
 * `currencyDisplay: 'narrowSymbol'` and `style: 'unit'` are both ES2020 and are
 * rejected by Safari < 14.1, Chrome < 77 and the Android WebViews shipped with
 * them ("RangeError: invalid value narrowSymbol for option currencyDisplay").
 * An unusable `currency` code throws here too. Since this module runs on the
 * page-init path via `useLocale`, an uncaught throw white-screens the pipe —
 * hence a degraded config rather than an exception.
 */
function firstSupportedFormat(
  locale: string,
  candidates: Intl.NumberFormatOptions[],
): Intl.NumberFormat | null {
  for (const options of candidates) {
    try {
      return new Intl.NumberFormat(locale, options)
    } catch {
      /* option set unsupported on this engine → try the next candidate */
    }
  }
  return null
}

/** Parts from the first accepted candidate; empty when the engine rejects all. */
function partsFor(
  locale: string,
  candidates: Intl.NumberFormatOptions[],
  sample: number,
): Intl.NumberFormatPart[] {
  return firstSupportedFormat(locale, candidates)?.formatToParts(sample) ?? []
}

function part(parts: Intl.NumberFormatPart[], type: string): string | undefined {
  return parts.find((p) => p.type === type)?.value
}

function extractNumberConfig(locale: string): NumberFormatConfig {
  const parts = partsFor(locale, [{}], -11111.1)
  return {
    thousandsSeparator: part(parts, 'group') ?? ',',
    decimalSeparator: part(parts, 'decimal') ?? '.',
  }
}

/**
 * Extract symbol/code + format rules from a currency formatToParts run.
 * `currencyDisplay: 'narrowSymbol'` → short symbol; `'code'` → long code.
 */
function extractCurrency(
  locale: string,
  currency: string,
  display: 'narrowSymbol' | 'code',
  numberCfg: NumberFormatConfig,
): { token: string; rules: CurrencyFormatRules } {
  const styled: Intl.NumberFormatOptions = { style: 'currency', currency }
  const parts = partsFor(
    locale,
    [
      { ...styled, currencyDisplay: display },
      // `narrowSymbol` is ES2020 and is REJECTED (not ignored) by older engines;
      // `symbol` is the pre-ES2020 default and is universally supported. It can
      // be wider for some locales (`CA$` where narrow gives `$`) — an accepted
      // degradation on those browsers, and admins can override the symbol.
      ...(display === 'narrowSymbol'
        ? [{ ...styled, currencyDisplay: 'symbol' } as Intl.NumberFormatOptions]
        : []),
      styled,
      // Last resort: an unusable `currency` code makes every `style: 'currency'`
      // set throw. Read the separators off a plain-number run at the near-
      // universal 2-decimal currency precision and let `token` fall back to the
      // raw code below.
      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    ],
    -1234.5,
  )
  const currencyIdx = parts.findIndex((p) => p.type === 'currency')
  const firstIntIdx = parts.findIndex((p) => p.type === 'integer')
  // No `currency` part means we fell through to the plain-number candidate, so
  // there is no ICU layout to read: keep its numeric facts and place the token
  // with the commonest layout instead of the `after` that an absent part implies.
  const symbolPosition: 'before' | 'after' =
    currencyIdx === -1
      ? 'before'
      : firstIntIdx !== -1 && currencyIdx < firstIntIdx
        ? 'before'
        : 'after'
  // A whitespace-only `literal` adjacent to the currency part = spaced symbol.
  const neighbour =
    currencyIdx === -1
      ? undefined
      : symbolPosition === 'before'
        ? parts[currencyIdx + 1]
        : parts[currencyIdx - 1]
  const spaceBetween = !!neighbour && neighbour.type === 'literal' && WS_RE.test(neighbour.value)
  const fraction = part(parts, 'fraction')
  return {
    token: part(parts, 'currency') ?? currency.toUpperCase(),
    rules: {
      decimalSeparator: part(parts, 'decimal') ?? numberCfg.decimalSeparator,
      thousandsSeparator: part(parts, 'group') ?? numberCfg.thousandsSeparator,
      decimals: fraction ? fraction.length : 0,
      spaceBetween,
      symbolPosition,
    },
  }
}

function unitNames(locale: string, unit: string): UnitNames {
  // `style: 'unit'` is ES2020 too, so it throws on the same older engines that
  // reject `narrowSymbol` — `partsFor` returns [] there and both forms fall back
  // to the hardcoded abbreviation / the bare unit key.
  //
  // Sample the long form at count 2 so the stored "name" is the general/plural
  // word (e.g. "centimeters", "feet", "kilograms") — matches the prior Intl
  // output for the common case. Narrow is count-independent.
  const long =
    part(partsFor(locale, [{ style: 'unit', unit, unitDisplay: 'long' }], 2), 'unit') ?? unit
  const narrow =
    part(partsFor(locale, [{ style: 'unit', unit, unitDisplay: 'narrow' }], 1), 'unit') ?? ''
  // CLDR narrow `pound` in en-* is the historical `#`; force a real abbreviation
  // when the narrow form isn't a letter. Same fallback covers empty parts.
  const short = /^\p{L}/u.test(narrow) ? narrow : (IMPERIAL_FALLBACK_SHORT[unit] ?? narrow)
  return { short, long }
}

const DERIVE_CACHE = new Map<string, LocaleConfig>()

/**
 * Derive the DEFAULT `LocaleConfig` for a locale + currency from `Intl`.
 * Memoised by `locale|currency` — `Intl` instances are expensive.
 */
export function deriveLocaleConfig(locale: string, currencyCode: string): LocaleConfig {
  const currency = (currencyCode || 'usd').toUpperCase()
  const cacheKey = `${locale}|${currency}`
  const cached = DERIVE_CACHE.get(cacheKey)
  if (cached) return cached

  const number = extractNumberConfig(locale)
  const short = extractCurrency(locale, currency, 'narrowSymbol', number)
  const long = extractCurrency(locale, currency, 'code', number)
  let region: string | undefined
  try {
    region = new Intl.Locale(locale).maximize().region
  } catch {
    region = undefined
  }

  const config: LocaleConfig = {
    number,
    currencyShort: short.rules,
    currencyLong: long.rules,
    currencySymbol: short.token,
    currencyCode: long.token,
    weight: {
      kilogram: unitNames(locale, 'kilogram'),
      pound: unitNames(locale, 'pound'),
    },
    height: {
      centimeter: unitNames(locale, 'centimeter'),
      foot: unitNames(locale, 'foot'),
      inch: unitNames(locale, 'inch'),
    },
    measurementSystem: IMPERIAL_REGIONS.has(region ?? '') ? 'imperial' : 'metric',
  }
  DERIVE_CACHE.set(cacheKey, config)
  return config
}

// Treat undefined and empty-string as "no override" so a blank admin field
// doesn't wipe a derived default.
function pick<T>(override: T | undefined, base: T): T {
  if (override === undefined || override === null) return base
  if (typeof override === 'string' && override === '') return base
  return override
}

function mergeRules(
  base: CurrencyFormatRules,
  o?: DeepPartial<CurrencyFormatRules>,
): CurrencyFormatRules {
  if (!o) return base
  return {
    decimalSeparator: pick(o.decimalSeparator, base.decimalSeparator),
    thousandsSeparator: pick(o.thousandsSeparator, base.thousandsSeparator),
    decimals: pick(o.decimals, base.decimals),
    spaceBetween: pick(o.spaceBetween, base.spaceBetween),
    symbolPosition: pick(o.symbolPosition, base.symbolPosition),
  }
}

function mergeUnit(base: UnitNames, o?: DeepPartial<UnitNames>): UnitNames {
  if (!o) return base
  return { short: pick(o.short, base.short), long: pick(o.long, base.long) }
}

/** `own` layered over `other`, ignoring the leaves `own` leaves unset. */
function layerRules(
  own?: DeepPartial<CurrencyFormatRules>,
  other?: DeepPartial<CurrencyFormatRules>,
): DeepPartial<CurrencyFormatRules> | undefined {
  if (!own) return other
  if (!other) return own
  const out: Record<string, unknown> = { ...other }
  for (const [k, v] of Object.entries(own)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v
  }
  return out as DeepPartial<CurrencyFormatRules>
}

/**
 * Deep-merge a resolved override onto a derived base. Override wins per-leaf;
 * undefined/empty leaves keep the base. Returns a fresh object (never mutates
 * `base`, which may be the shared derive cache entry).
 */
export function mergeLocaleConfig(
  base: LocaleConfig,
  override?: ResolvedLocaleOverride | null,
): LocaleConfig {
  if (!override) return base
  const currencySymbol = pick(override.currencySymbol, base.currencySymbol ?? '')
  const currencyCode = pick(override.currencyCode, base.currencyCode ?? '')
  // Many currencies have no distinct symbol — ICU's short (narrowSymbol) form
  // for CHF is the string `CHF`, identical to its code. Both forms then render
  // the same text, so an author cannot tell which of the two rule sets drives
  // their prices, and naturally reaches for the section NAMED after what they
  // see ("long (code)"). When the two tokens coincide, treat the sections as
  // one: either one's settings govern both forms, per leaf, with a section's
  // own value still winning for its own form. Currencies with a real symbol
  // (`$` vs `USD`) keep the two rule sets fully independent.
  const sharedToken =
    !!currencySymbol && currencySymbol.trim().toUpperCase() === currencyCode.trim().toUpperCase()
  const shortOverride = sharedToken
    ? layerRules(override.currencyShort, override.currencyLong)
    : override.currencyShort
  const longOverride = sharedToken
    ? layerRules(override.currencyLong, override.currencyShort)
    : override.currencyLong
  return {
    number: {
      decimalSeparator: pick(override.number?.decimalSeparator, base.number.decimalSeparator),
      thousandsSeparator: pick(override.number?.thousandsSeparator, base.number.thousandsSeparator),
    },
    currencyShort: mergeRules(base.currencyShort, shortOverride),
    currencyLong: mergeRules(base.currencyLong, longOverride),
    currencySymbol,
    currencyCode,
    weight: {
      kilogram: mergeUnit(base.weight.kilogram, override.weight?.kilogram),
      pound: mergeUnit(base.weight.pound, override.weight?.pound),
    },
    height: {
      centimeter: mergeUnit(base.height.centimeter, override.height?.centimeter),
      foot: mergeUnit(base.height.foot, override.height?.foot),
      inch: mergeUnit(base.height.inch, override.height?.inch),
    },
    measurementSystem: pick(override.measurementSystem, base.measurementSystem),
  }
}

/**
 * One-shot: resolve the locale, derive defaults for the currency, and merge a
 * resolved (single-currency) override. The single entry point used by
 * `useLocale` and by the editor TipTap components.
 */
export function buildLocaleConfig(
  language?: string | null,
  geography?: string | null,
  currencyCode?: string | null,
  override?: ResolvedLocaleOverride | null,
): LocaleConfig {
  const base = deriveLocaleConfig(resolveLocale(language, geography), currencyCode || 'usd')
  return mergeLocaleConfig(base, override)
}

// ----------------------------------------------------------------------------
// Pure formatters (replace the Intl-direct paths in the render engine)
// ----------------------------------------------------------------------------

function groupThousands(intDigits: string, sep: string): string {
  return sep ? intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : intDigits
}

/**
 * Format a number with config separators. When `decimals` is provided the value
 * is fixed to that precision; otherwise its natural decimals are kept.
 */
export function formatNumber(value: unknown, cfg: LocaleConfig, decimals?: number | null): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value ?? '')
  const neg = num < 0
  const abs = Math.abs(num)
  const fixed = decimals === undefined || decimals === null ? String(abs) : abs.toFixed(decimals)
  const [intPart = '', fracPart] = fixed.split('.')
  let out = groupThousands(intPart, cfg.number.thousandsSeparator)
  if (fracPart) out += cfg.number.decimalSeparator + fracPart
  return neg ? `-${out}` : out
}

function assembleCurrency(value: number, rules: CurrencyFormatRules, token: string): string {
  const neg = value < 0
  const abs = Math.abs(value)
  const fixed = abs.toFixed(rules.decimals)
  const [intPart = '', fracPart] = fixed.split('.')
  let num = groupThousands(intPart, rules.thousandsSeparator)
  if (fracPart) num += rules.decimalSeparator + fracPart
  const gap = rules.spaceBetween ? ' ' : ''
  const sign = neg ? '-' : ''
  return rules.symbolPosition === 'before'
    ? `${sign}${token}${gap}${num}`
    : `${sign}${num}${gap}${token}`
}

/** Format an amount with the short (symbol) form. */
export function formatCurrencyShort(value: unknown, cfg: LocaleConfig): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value ?? '')
  const token = cfg.currencySymbol ?? cfg.currencyCode ?? ''
  return assembleCurrency(num, cfg.currencyShort, token)
}

/** Format an amount with the long (code) form. */
export function formatCurrencyLong(value: unknown, cfg: LocaleConfig): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value ?? '')
  const token = cfg.currencyCode ?? cfg.currencySymbol ?? ''
  return assembleCurrency(num, cfg.currencyLong, token)
}
